import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Camera,
  CheckCircle2,
  ExternalLink,
  Loader2,
  MessageSquare,
  PenLine,
  Pencil,
  ShoppingBag,
  Wrench,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { formatUsd } from '@/lib/format';
import { ReorderButton, reorderMessage } from '@/components/orders/ReorderButton';
import { OrderTracker } from '@/components/orders/OrderTracker';
import type { ReorderSummary } from '@/hooks/useReorder';
import { useCart } from '@/hooks/useCart';
import { useCustomer } from '@/hooks/useCustomer';
import { useSfJobConfirmation } from '@/hooks/useSfJobConfirmation';
import { useSession } from '@/lib/auth';
import { useCreateServiceRequest } from '@/hooks/useServiceRequests';

interface OrderDetail {
  id: string;
  order_number: string | null;
  customer_location_id: string;
  po_number: string | null;
  delivery_notes: string | null;
  status: string;
  subtotal: number;
  estimated_total: number;
  sf_job_id: string | null;
  sf_job_status: string | null;
  delivered_at: string | null;
  qbo_invoice_id: string | null;
  submitted_at: string | null;
  created_at: string;
  /* Change-order links (1.141). A change REPLACES an order, so an order is
     either the one that was replaced or the replacement. */
  supersedes_order_id: string | null;
  superseded_by_order_id: string | null;
  supersede_notified_at: string | null;
}

/** The order on the other end of a change — whichever direction it points. */
interface LinkedOrder {
  id: string;
  order_number: string | null;
  sf_job_id: string | null;
  status: string;
}

interface OrderLine {
  item_name: string;
  sku: string | null;
  quantity: number;
  unit_price: number;
  line_total: number;
  line_number: number;
}

export default function Confirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ order_id?: string }>();
  const [searchParams] = useSearchParams();
  // Prefer path param (stable /orders/:id URL); fall back to query param
  // (immediately after submit).
  const orderId = params.order_id ?? searchParams.get('order_id');
  const sfJobIdFromQuery = searchParams.get('sf_job_id');
  // Only clear the cart on the post-submit confirmation route — not when
  // a customer returns to /orders/:id from an email days later.
  const isPostSubmitView = location.pathname.startsWith('/confirmation');
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [linked, setLinked] = useState<LinkedOrder | null>(null);
  /* ⚠ "We couldn't find that order" used to be shown for ANY failed read, so a
     400 from a malformed id (the `order:<uuid>` composite-key bug) was
     indistinguishable from an order that genuinely isn't there. Keep the
     reason. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMessage, setShowMessage] = useState(false);
  const [reorderNotice, setReorderNotice] = useState<{
    tone: 'success' | 'warning';
    text: string;
  } | null>(null);
  const { clear } = useCart();

  /* Whether this order can be changed. Mirrors _lib/order-supersede.ts: the
     status set is order-lifecycle-check's SF watch list, so the old job's
     deletion is verifiable; an order already replaced is out, or a change
     would chain a third job onto a stale one.

     ⚠ `status` alone is NOT the test, and offering this button on that basis
     was a live bug: nothing ever writes 'delivered', so **153 of 200 orders**
     are picked up and invoiced while still reading 'submitted'. A finished
     order has to be caught by `delivered_at` / `qbo_invoice_id` / a terminal
     `sf_job_status`, or the button appears on an order that shipped weeks ago
     and pressing it would ask dispatch to delete an invoiced job. */
  const wasReplaced = Boolean(order?.superseded_by_order_id);
  const isReplacement = Boolean(order?.supersedes_order_id);
  const isFinished =
    order != null &&
    (order.delivered_at != null ||
      order.qbo_invoice_id != null ||
      ['completed', 'closed', 'cancelled', 'invoiced', 'done'].includes(
        (order.sf_job_status ?? '').trim().toLowerCase(),
      ));
  const canChangeOrder =
    order != null &&
    !wasReplaced &&
    !isFinished &&
    ['submitted', 'accepted', 'scheduled'].includes(order.status);

  // Proof of delivery (signature + photos) from Service Fusion, keyed by the
  // order's SF job. This is what the "View delivery photos & signatures" link
  // in the delivered email points at, so it must render here on the order page.
  const { data: confirmation } = useSfJobConfirmation(
    order?.sf_job_id ?? sfJobIdFromQuery ?? null,
  );

  useEffect(() => {
    if (isPostSubmitView) clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPostSubmitView]);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      const [oRes, lRes] = await Promise.all([
        supabase
          .schema('orders')
          .from('orders')
          .select(
            'id, order_number, customer_location_id, po_number, delivery_notes, status, subtotal, estimated_total, sf_job_id, sf_job_status, delivered_at, qbo_invoice_id, submitted_at, created_at, supersedes_order_id, superseded_by_order_id, supersede_notified_at',
          )
          .eq('id', orderId)
          .single(),
        supabase
          .schema('orders')
          .from('order_lines')
          .select('item_name, sku, quantity, unit_price, line_total, line_number')
          .eq('order_id', orderId)
          .order('line_number'),
      ]);
      const o = (oRes.data as OrderDetail | null) ?? null;
      if (o) {
        setOrder(o);
        setLoadError(null);
      } else if (oRes.error && oRes.error.code !== 'PGRST116') {
        // PGRST116 is .single()'s "no rows" — a real not-found. Anything else
        // is a fault worth naming rather than dressing up as a missing order.
        // eslint-disable-next-line no-console
        console.error('[order] load failed:', oRes.error);
        setLoadError(oRes.error.message || 'We could not load that order.');
      } else {
        setLoadError(null);
      }
      if (lRes.data) setLines(lRes.data as OrderLine[]);

      /* The order on the other end of a change, so the page can NAME it rather
         than printing a uuid. One extra read, and only when there is a link —
         RLS scopes it to this account, and a change is always within one. */
      const otherId = o?.superseded_by_order_id ?? o?.supersedes_order_id ?? null;
      setLinked(null);
      if (otherId) {
        const { data } = await supabase
          .schema('orders')
          .from('orders')
          .select('id, order_number, sf_job_id, status')
          .eq('id', otherId)
          .maybeSingle();
        if (data) setLinked(data as LinkedOrder);
      }
      setLoading(false);
    })();
  }, [orderId]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-brix-muted">
          <Loader2 size={16} className="animate-spin" /> Loading order…
        </div>
      </div>
    );
  }

  if (!orderId || !order) {
    return (
      <div className="h-full flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <ShoppingBag size={28} className="mx-auto mb-3 text-brix-muted" />
          <p className="text-sm text-brix-ink font-medium">No order to show</p>
          <p className="text-xs text-brix-muted mt-1 mb-4">
            {!orderId
              ? 'Place an order from the catalog to land here.'
              : loadError
                ? `We couldn't load that order — ${loadError}`
                : "We couldn't find that order. It may belong to another account."}
          </p>
          <Button onClick={() => navigate('/order')}>Browse catalog</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-20 md:pb-10">
      <div className="max-w-xl mx-auto px-4 md:px-8 pt-8 pb-12">
        <div className="text-center mb-6">
          <div
            className="w-14 h-14 rounded-full bg-brix-success/10 flex items-center justify-center mx-auto mb-3"
            aria-hidden
          >
            <CheckCircle2 size={32} className="text-brix-success" />
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-brix-ink">
            {isPostSubmitView ? 'Order submitted' : 'Order status'}
          </h1>
          <p className="text-sm text-brix-muted mt-1">
            {isPostSubmitView
              ? "Thanks — we'll get this dispatched. A confirmation email is on its way."
              : 'Current status of this order.'}
          </p>
        </div>

        {/* Live delivery tracker — reads SF status via the customer-scoped
            order-tracking function. Degrades gracefully when SF is quiet. */}
        <div className="mb-4">
          <OrderTracker orderId={order.id} />
        </div>

        <div className="rounded-2xl border border-brix-border bg-brix-surface shadow-card p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Order #" value={order.order_number ?? order.id.slice(0, 8)} mono />
            <Field
              label="SF Job #"
              value={sfJobIdFromQuery ?? order.sf_job_id ?? '—'}
              mono
            />
            <Field label="Status" value={order.status} />
            <Field
              label="Submitted"
              value={
                order.submitted_at
                  ? new Date(order.submitted_at).toLocaleString()
                  : '—'
              }
            />
            {order.po_number && <Field label="PO #" value={order.po_number} mono />}
            {order.delivery_notes && (
              <Field label="Notes" value={order.delivery_notes} full />
            )}
          </div>

          <div className="pt-3 border-t border-brix-border">
            <p className="text-[10px] tracking-tight text-brix-muted font-semibold mb-2">
              Line items
            </p>
            <ul className="divide-y divide-brix-border -my-2">
              {lines.map((l) => (
                <li key={l.line_number} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brix-ink truncate">
                      {l.item_name}
                    </p>
                    <p className="text-[11px] text-brix-muted">
                      {l.sku && <span className="font-mono mr-2">{l.sku}</span>}
                      Qty {l.quantity} × {formatUsd(l.unit_price)}
                    </p>
                  </div>
                  <p className="text-sm font-mono font-semibold text-brix-ink whitespace-nowrap">
                    {formatUsd(l.line_total)}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-brix-border">
            <p className="font-display text-base font-semibold text-brix-ink">
              Estimated total
            </p>
            <p className="font-display text-2xl font-semibold font-mono text-brix-primary">
              {formatUsd(order.estimated_total)}
            </p>
          </div>
        </div>

        {confirmation?.hasData &&
          (confirmation.signatureUrl || confirmation.photos.some((p) => p.is_image)) && (
            <div className="rounded-2xl border border-brix-border bg-brix-surface shadow-card p-5 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Camera size={15} className="text-brix-primary" />
                <h2 className="font-display text-base font-semibold text-brix-ink">
                  Delivery photos &amp; signature
                </h2>
              </div>

              {confirmation.photos.filter((p) => p.is_image).length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {confirmation.photos
                    .filter((p) => p.is_image)
                    .map((p, i) => (
                      <a
                        key={p.url}
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block aspect-square rounded-lg overflow-hidden border border-brix-border bg-brix-surface-alt hover:opacity-90 transition-opacity"
                      >
                        <img
                          src={p.url}
                          alt={p.caption ?? `Delivery photo ${i + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </a>
                    ))}
                </div>
              )}

              {confirmation.signatureUrl && (
                <div>
                  <p className="text-[10px] tracking-tight text-brix-muted font-semibold mb-1 flex items-center gap-1.5">
                    <PenLine size={12} /> Signature
                    {confirmation.signerName && (
                      <span className="text-brix-ink font-normal normal-case">
                        · {confirmation.signerName}
                      </span>
                    )}
                  </p>
                  <div className="rounded-lg border border-brix-border bg-white p-2 inline-block">
                    <img
                      src={confirmation.signatureUrl}
                      alt="Delivery signature"
                      className="max-h-24"
                    />
                  </div>
                </div>
              )}

              {confirmation.completedAt && (
                <p className="text-[11px] text-brix-muted mt-3">
                  Delivered {new Date(confirmation.completedAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

        {reorderNotice && (
          <div
            role="status"
            className={`mt-4 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium ${
              reorderNotice.tone === 'success'
                ? 'border-brix-primary/30 bg-brix-primary-tint/60 text-brix-primary'
                : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}
          >
            {reorderNotice.tone === 'success' ? (
              <CheckCircle2 size={15} className="shrink-0" />
            ) : (
              <X size={15} className="shrink-0" />
            )}
            <span>{reorderNotice.text}</span>
          </div>
        )}

        {/* A change REPLACES the order, so both ends of that link have to say
            so. Without this the replaced order still reads as live and the
            natural next move is to chase it or place a third order. */}
        {wasReplaced && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="flex items-start gap-2.5">
              <Pencil size={15} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">This order was replaced</p>
                <p className="text-[13px] text-amber-800 mt-0.5 leading-relaxed">
                  You changed it, so we placed{' '}
                  {linked ? (
                    <Link
                      to={`/orders/${linked.id}`}
                      className="font-semibold underline hover:no-underline"
                    >
                      {linked.order_number ?? 'a corrected order'}
                    </Link>
                  ) : (
                    'a corrected order'
                  )}{' '}
                  instead — that's the one we're filling.{' '}
                  {order.supersede_notified_at
                    ? "We've asked our team to cancel this one so you only get one delivery."
                    : "⚠ We couldn't notify our team to cancel this one — please give us a call so you aren't sent both."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* And the replacement says what it replaced, so the two order numbers
            in the customer's inbox are explainable. */}
        {isReplacement && (
          <div className="mt-4 rounded-xl border border-brix-border bg-brix-surface-alt/50 px-4 py-3">
            <p className="text-[13px] text-brix-muted leading-relaxed">
              <Pencil size={13} className="inline-block mr-1.5 -mt-0.5 text-brix-muted" />
              This is the corrected version of{' '}
              {linked ? (
                <Link
                  to={`/orders/${linked.id}`}
                  className="font-semibold text-brix-ink underline hover:no-underline"
                >
                  {linked.order_number ?? 'an earlier order'}
                </Link>
              ) : (
                'an earlier order'
              )}
              , which we cancelled. One delivery, not two.
            </p>
          </div>
        )}

        {/* Change this order. Only while there is still a live job to change —
            past that it is a credit or a billing correction, not an edit. */}
        {canChangeOrder && (
          <div className="mt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/orders/${order.id}/change`)}
              className="w-full"
            >
              <Pencil size={14} /> Change this order
            </Button>
          </div>
        )}

        {lines.length > 0 && (
          <div className="mt-4">
            <ReorderButton
              orderId={order.id}
              label="Reorder these items"
              variant="secondary"
              size="md"
              className="w-full"
              onResult={(summary: ReorderSummary | null) =>
                setReorderNotice({
                  tone: summary && summary.added > 0 ? 'success' : 'warning',
                  text: reorderMessage(summary),
                })
              }
            />
          </div>
        )}

        {order.qbo_invoice_id && (
          <div className="mt-4">
            <Button
              type="button"
              onClick={() => navigate(`/invoices/${order.qbo_invoice_id}`)}
              className="w-full"
            >
              View &amp; pay invoice
            </Button>
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowMessage(true)}
            className="flex-1"
          >
            <MessageSquare size={14} /> Message about this order
          </Button>
        </div>

        <div className="flex gap-2 mt-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/home')}
            className="flex-1"
          >
            Back to dashboard
          </Button>
          <Button type="button" onClick={() => navigate('/order')} className="flex-1">
            Start another order <ExternalLink size={14} />
          </Button>
        </div>
      </div>

      {showMessage && order && (
        <OrderMessageModal order={order} onClose={() => setShowMessage(false)} />
      )}
    </div>
  );
}

/* ------------------------- message-about-order modal ------------------------- */

function OrderMessageModal({
  order,
  onClose,
}: {
  order: OrderDetail;
  onClose: () => void;
}) {
  const { session } = useSession();
  const { data: ctx } = useCustomer();
  const create = useCreateServiceRequest();
  const orderTag = order.order_number ?? order.id.slice(0, 8);

  const [subject, setSubject] = useState(`Question about order ${orderTag}`);
  const [body, setBody] = useState('');
  const [contactName, setContactName] = useState(ctx?.user.fullName ?? '');
  const [contactPhone, setContactPhone] = useState(ctx?.user.phone ?? '');
  const [contactEmail, setContactEmail] = useState(session?.user?.email ?? '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!ctx) return;
    if (!subject.trim() || !body.trim()) return;
    if (!contactName.trim() || !contactPhone.trim()) return;

    const lines = [
      `Order: ${orderTag}`,
      order.sf_job_id ? `Order reference: ${order.sf_job_id}` : null,
      `Status: ${order.status}`,
      `Submitted: ${order.submitted_at ?? order.created_at}`,
      '',
      `Contact: ${contactName.trim()} · ${contactPhone.trim()}` +
        (contactEmail.trim() ? ` · ${contactEmail.trim()}` : ''),
      '',
      'Message:',
      body.trim(),
    ].filter((l) => l !== null) as string[];

    create.mutate(
      {
        customerId: ctx.customer.id,
        locationId: order.customer_location_id ?? ctx.locations[0]?.id ?? null,
        kind: 'other',
        subject: subject.trim(),
        body: lines.join('\n'),
      },
      { onSuccess: onClose },
    );
  }

  const errMsg = create.error instanceof Error ? create.error.message : null;
  const canSubmit =
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    contactName.trim().length > 0 &&
    contactPhone.trim().length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-msg-heading"
      className="fixed inset-0 z-[110] flex items-start md:items-center justify-center px-4 py-8 overflow-y-auto"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-card-deep overflow-hidden border-2 border-brix-primary-deep">
        <div className="bg-brix-primary-deep px-6 py-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center text-white shrink-0">
              <MessageSquare size={18} />
            </div>
            <div className="min-w-0">
              <h2
                id="order-msg-heading"
                className="font-display text-lg font-bold tracking-tight text-white leading-none"
              >
                Message about this order
              </h2>
              <p className="text-xs text-white/85 tracking-tight mt-1 truncate font-mono">
                {orderTag}
                {order.sf_job_id && ` · SF #${order.sf_job_id}`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-white hover:bg-white/15 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="om-subject" className="text-xs tracking-tight font-semibold text-slate-900">
              Subject
            </Label>
            <Input
              id="om-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={140}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="om-body" className="text-xs tracking-tight font-semibold text-slate-900">
              Your message
            </Label>
            <textarea
              id="om-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              maxLength={2000}
              placeholder="Need to change the address, push the delivery, swap a SKU? Tell us here."
              className="w-full rounded-md border border-brix-border bg-brix-surface px-3 py-2 text-sm leading-relaxed placeholder:text-brix-muted/70 focus-visible:outline-none focus-visible:border-brix-primary focus-visible:ring-2 focus-visible:ring-brix-primary/20"
              required
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-tight font-semibold text-slate-900">Contact name</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-tight font-semibold text-slate-900">Callback number</Label>
              <Input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-tight font-semibold text-slate-900">Email</Label>
              <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            </div>
          </div>
          {errMsg && (
            <div className="text-xs text-red-600 tracking-tight px-3 py-2 rounded-md border border-red-200 bg-red-50">
              {errMsg}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={create.isPending || !canSubmit}>
              {create.isPending ? <Loader2 size={13} className="animate-spin" /> : <Wrench size={13} />}
              {create.isPending ? 'Sending…' : 'Send message'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  full,
}: {
  label: string;
  value: string;
  mono?: boolean;
  full?: boolean;
}) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <p className="text-[10px] tracking-tight text-brix-muted font-semibold">
        {label}
      </p>
      <p className={`mt-0.5 text-sm text-brix-ink ${mono ? 'font-mono' : ''}`}>
        {value}
      </p>
    </div>
  );
}
